import React, {useMemo} from 'react';
import {Text, TouchableOpacity, View} from 'react-native';
import {useSelector} from 'react-redux';
import {images} from '../../../assets/icons';
import {contentSelector} from '../../../redux/selectors/resources';
import {skipassClientInfoSelector} from '../../../redux/selectors/skipass';
import {Markdown} from '../../../shared/components/markdown';
import {ResponsiveView} from '../../../shared/components/responsiveView';
import {useStyles} from '../../../shared/hooks/styles';
import {useIsDesktop} from '../../../shared/hooks/useIsDesktop';
import {DeviceSize} from '../../../shared/theme/helpers';
import {formatCurrency} from '../../../shared/utils/numberFormatToRub';
import {OperationsTabs} from './OperationsTabs';

interface RegisteredSkipassProps {
  onBlockPress: () => void;
}

export function RegisteredSkipass({onBlockPress}: RegisteredSkipassProps) {
  const content = useSelector(contentSelector());
  const isDesktop = useIsDesktop();
  const {clientInfo} = useSelector(skipassClientInfoSelector());

  const depositLabel = useMemo(
    () =>
      formatCurrency(clientInfo?.clientAccountInfo.deposit.split(',')[0], {
        maximumFractionDigits: 0,
        currencyDisplay: '₽',
      }),
    [clientInfo],
  );

  const styles = useStyles(({theme, mediaOnly}) => ({
    contentTextWrapper: mediaOnly(DeviceSize.desktop, {
      maxWidth: '80%',
    }),

    bottomContainer: {
      marginTop: 40,
      ...mediaOnly(DeviceSize.mobile, {marginTop: 0}),
    },

    tabsTitle: {
      ...theme.fonts.h3,
      marginBottom: 20,
    },

    skipassInfo: {
      marginBottom: 40,
      borderWidth: 1,
      borderColor: theme.color.borderColor,
      backgroundColor: theme.color.primaryNonGreyBackground,
      shadowColor: theme.color.primaryBlackColor,
      shadowOpacity: 0.05,
      shadowOffset: {width: 0, height: 4},
      shadowRadius: 10,
      elevation: 5,
      paddingHorizontal: 40,
      paddingVertical: 40,
      flexDirection: 'row',
      alignItems: 'flex-start',

      ...mediaOnly(
        DeviceSize.desktop,
        {
          width: '85%',
          borderRadius: 20,
        },
        {
          width: '100%',
          marginBottom: 20,
          alignItems: 'stretch',
          borderRadius: 5,
        },
      ),
      ...mediaOnly(
        DeviceSize.mobile,
        {
          flexDirection: 'column',
          justifyContent: 'flex-start',
          paddingHorizontal: 5,
          paddingVertical: 15,
        },
        {flexDirection: 'row'},
      ),
    },

    infoCell: {
      marginHorizontal: 15,

      ...mediaOnly(DeviceSize.desktop, {maxWidth: 300}),
      ...mediaOnly(DeviceSize.mobile, {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 10,
      }),
    },

    infoTitleContainer: {
      marginBottom: 10,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },

    infoTitle: {
      ...theme.fonts.bodySmallLight,
      color: '#828282',

      ...mediaOnly(DeviceSize.mobile, {
        marginRight: 15,
        marginBottom: 0,
        flexShrink: 0,
      }),
    },

    infoText: {
      color: theme.color.primaryColor,
      ...theme.fonts.h3,

      ...mediaOnly(DeviceSize.mobile, {
        marginLeft: 'auto',
        fontSize: 16,
        flexShrink: 1,
      }),
    },

    infoTextTarif: {
      color: theme.color.textColor,

      ...mediaOnly(DeviceSize.mobile, {
        ...theme.fonts.bodySmallLight,
        fontSize: 16,
      }),
    },
  }));

  return (
    <View>
      <View style={styles.skipassInfo}>
        <View style={styles.infoCell}>
          <View style={styles.infoTitleContainer}>
            <Text style={styles.infoTitle}>Ски-пасс</Text>
            {!isDesktop && (
              <TouchableOpacity onPress={onBlockPress}>
                <images.SignOut color='#828282' width={18} height={18} />
              </TouchableOpacity>
            )}
          </View>
          <Text style={styles.infoText}>{clientInfo?.clientAccountInfo.accountNumber ?? '—'}</Text>
        </View>

        <View style={styles.infoCell}>
          <View style={styles.infoTitleContainer}>
            <Text style={styles.infoTitle}>Баланс</Text>
          </View>
          <Text style={styles.infoText}>{depositLabel}</Text>
        </View>

        <View style={[styles.infoCell, {maxWidth: 'auto', flexShrink: 1}]}>
          <View style={styles.infoTitleContainer}>
            <Text style={styles.infoTitle}>Тариф</Text>
          </View>
          <Text style={[styles.infoText, styles.infoTextTarif]}>
            {clientInfo?.clientAccountInfo.singleTickets.find((ticket) => ticket.isValid)?.name ||
              clientInfo?.clientAccountInfo.seasonTickets.find((ticket) => ticket.isValid)?.name ||
              '—'}
          </Text>
        </View>
      </View>

      <ResponsiveView exclude={[DeviceSize.mobile]}>
        <View style={styles.contentTextWrapper}>
          <Markdown listColumns={1}>{content.services.skipass.activeFeatureDescription}</Markdown>
        </View>
      </ResponsiveView>

      <View style={styles.bottomContainer}>
        <ResponsiveView exclude={[DeviceSize.mobile]}>
          <Text style={styles.tabsTitle}>История</Text>
        </ResponsiveView>

        <OperationsTabs />
      </View>
    </View>
  );
}
